// ReissueTicketModal.jsx
// Shared modal for re-issuing a golden ticket via email or thermal printer.
// Used by both ArtistQueueView (active shift) and ShiftArchiveView (past shifts).

import { useState } from "react";
import { db } from "../firebase";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { printJobs } from "../utils/printer";
import { computeDisplayTitle } from "../utils/meditations";
import { STAFF_THEME } from "../theme/staff";

const BRAND = STAFF_THEME;

export default function ReissueTicketModal({ order, onClose }) {
  const [mode, setMode]       = useState(null); // null | "email" | "print"
  const [email, setEmail]     = useState("");
  const [busy, setBusy]       = useState(false);
  const [pct, setPct]         = useState(0);
  const [printMsg, setPrintMsg] = useState("");
  const [result, setResult]   = useState(null); // "sent" | "printed" | error string

  const name     = order.recipientName || order.subjectName || order.customerName || "Unknown";
  const orderUrl = `https://thejoyofpancakes.com/order?t=${order.tokenId}`;

  async function sendEmail() {
    if (!email.trim()) return;
    setBusy(true); setPct(15);
    try {
      setPct(35);
      const sendFn = httpsCallable(getFunctions(getApp()), "sendTicketEmail");
      await sendFn({
        email: email.trim(),
        jobs: [{
          ticketType: order.orderType || "custom",
          orderUrl,
          expiryDisplay: "Re-issued ticket",
        }],
      });
      setPct(75);
      await updateDoc(doc(db, "orders", order.id), {
        reissuedAt: serverTimestamp(),
        reissueMethod: "email",
        reissueEmail: email.trim(),
      });
      setPct(100);
      setResult("sent");
    } catch (err) {
      setResult(`Error: ${err.message}`);
    }
    setBusy(false);
  }

  async function reprint() {
    setBusy(true); setPct(5); setPrintMsg("Loading printer SDK…");
    try {
      // Placard fields: piece title (computeDisplayTitle covers flash/freestyle/
      // untitled; titled customs carry subjectName verbatim — never a person's
      // name as fallback) + the year it hit the griddle.
      const title = computeDisplayTitle(order) || (order.subjectName || "").trim() || null;
      const year  = (order.submittedAt || order.createdAt)?.toDate?.().getFullYear() || null;
      await printJobs(
        [{ ticketType: "reissue", orderUrl, title, year }],
        {
          connectTimeoutMs: 15000,
          onStatus: ({ pct: p, msg }) => { setPct(p); setPrintMsg(msg); },
        }
      );
      setPrintMsg("Saving…");
      await updateDoc(doc(db, "orders", order.id), {
        reissuedAt: serverTimestamp(),
        reissueMethod: "print",
      });
      setPct(100);
      setResult("printed");
    } catch (err) {
      setResult(`Error: ${err.message}`);
    }
    setBusy(false);
  }

  function tryAgain() {
    setResult(null);
    setPct(0);
    // Email mode returns to its form; print mode re-fires immediately —
    // clearing the error alone would strand a dead progress view.
    if (mode === "print") reprint();
  }

  return (
    <div onClick={() => !busy && onClose()} style={{
      position: "fixed", inset: 0, zIndex: 100,
      background: "rgba(0,0,0,0.88)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "1rem",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: BRAND.panel, borderRadius: "16px", border: `1px solid ${BRAND.border}`,
        padding: "1.5rem", maxWidth: "380px", width: "100%",
        display: "flex", flexDirection: "column", gap: "1rem",
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "1.8rem", marginBottom: "0.3rem" }}>🎟</div>
          <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1.05rem" }}>Re-issue Ticket</div>
          <div style={{ color: BRAND.muted, fontSize: "0.82rem", marginTop: "0.25rem" }}>
            #{order.queueNumber} · {name}
          </div>
        </div>

        {result === "sent" || result === "printed" ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ color: "#70be44", fontSize: "1rem", fontWeight: "bold", marginBottom: "0.5rem" }}>
              {result === "sent" ? "✓ Email sent!" : "✓ Ticket printed!"}
            </div>
            <button onClick={onClose} style={{ background: BRAND.gold, color: BRAND.dark, border: "none", borderRadius: "10px", padding: "0.7rem 2rem", fontWeight: "bold", fontSize: "0.9rem", cursor: "pointer" }}>
              Done
            </button>
          </div>
        ) : result ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ color: "#ef537e", fontSize: "0.85rem", marginBottom: "0.5rem" }}>{result}</div>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
              <button onClick={tryAgain} style={{ background: BRAND.gold, color: BRAND.dark, border: "none", borderRadius: "8px", padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "bold", cursor: "pointer" }}>
                Try again
              </button>
              <button onClick={onClose} style={{ background: BRAND.card, color: "white", border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.5rem 1rem", fontSize: "0.85rem", cursor: "pointer" }}>
                Close
              </button>
            </div>
          </div>
        ) : !mode ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            <button onClick={() => setMode("email")} style={{ background: BRAND.sky, color: "white", border: "none", borderRadius: "10px", padding: "0.85rem", fontSize: "0.95rem", fontWeight: "bold", cursor: "pointer" }}>
              📧 Send via Email
            </button>
            <button onClick={() => { setMode("print"); reprint(); }} style={{ background: BRAND.gold, color: BRAND.dark, border: "none", borderRadius: "10px", padding: "0.85rem", fontSize: "0.95rem", fontWeight: "bold", cursor: "pointer" }}>
              🖨 Reprint Ticket
            </button>
            <button onClick={onClose} style={{ background: "transparent", color: BRAND.muted, border: `1px solid ${BRAND.border}`, borderRadius: "10px", padding: "0.65rem", fontSize: "0.85rem", cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        ) : mode === "email" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            <input
              type="email" placeholder="Customer's email"
              value={email} onChange={e => setEmail(e.target.value)}
              autoFocus
              style={{ background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: "white", fontSize: "0.9rem", padding: "0.7rem", outline: "none" }}
            />
            <button onClick={sendEmail} disabled={busy || !email.trim()} style={{
              background: email.trim() && !busy ? BRAND.sky : "rgba(255,255,255,0.1)",
              color: email.trim() && !busy ? "white" : "rgba(255,255,255,0.3)",
              border: "none", borderRadius: "10px", padding: "0.75rem", fontSize: "0.9rem", fontWeight: "bold", cursor: email.trim() ? "pointer" : "not-allowed",
            }}>
              {busy ? "Sending\u2026" : "Send Ticket Email"}
            </button>
            {busy && (
              <div style={{ height: "6px", borderRadius: "3px", background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pct}%`, background: BRAND.sky, borderRadius: "3px", transition: "width 0.4s ease" }} />
              </div>
            )}
            <button onClick={() => setMode(null)} disabled={busy} style={{ background: "transparent", color: BRAND.muted, border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.5rem", fontSize: "0.82rem", cursor: "pointer" }}>
              ← Back
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <div style={{ textAlign: "center", color: BRAND.muted, fontSize: "0.85rem" }}>
              {printMsg || "Connecting to printer…"}
            </div>
            <div style={{ height: "6px", borderRadius: "3px", background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: BRAND.gold, borderRadius: "3px", transition: "width 0.4s ease" }} />
            </div>
            <button onClick={onClose} style={{ background: "transparent", color: BRAND.muted, border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.5rem", fontSize: "0.82rem", cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        )}

        <a href="http://192.168.1.69" target="_blank" rel="noopener noreferrer" style={{ color: BRAND.dim, fontSize: "0.7rem", textAlign: "center", display: "block", textDecoration: "none" }}>
          🔧 Printer not connecting? <span style={{ textDecoration: "underline" }}>Open printer web config</span>
        </a>

        {order.reissuedAt && (
          <div style={{ color: BRAND.dim, fontSize: "0.7rem", textAlign: "center", borderTop: `1px solid ${BRAND.divider}`, paddingTop: "0.5rem" }}>
            Previously re-issued via {order.reissueMethod} {order.reissuedAt?.toDate ? `on ${order.reissuedAt.toDate().toLocaleDateString()}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}
